import { TrackCode } from '@backrs_ui/common-ui';

import {
  CollectedData,
  LubavSessionType,
  MainResponse,
  ResourcesContent,
  SolutionSessionMessage,
} from '@backrs_ui/grow-ui/src/models/models';

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  baseQueryWithAuth,
  baseQueryWithCustomToken,
  lubavAppBaseQueryWithCustomToken,
} from '../baseQuery';
import { GetOfficeHoursSessionDataResponse } from '../coachLubav/coachLubav.api';
import { MostRecentResponseSetAPIResponse } from '../userResponse/thunks/userResponse.thunk';

export type TrackAssessmentSummary = Readonly<{
  uuid: string;
  career_track_code: TrackCode;
  gpa: number;
  summary_keywords: {
    category_slug: string;
    strength_keywords: string[];
    opportunity_keywords: string[];
  }[];
}>;

// The resource kinds the API will issue a share for -- see ALLOWED_RESOURCE_TYPES on the API side.
export type SharedResourceType =
  | 'blueprint'
  | 'account'
  | 'solution_session'
  | 'office_hours'
  | 'deep_dive'
  | 'coach';

export type SessionExchangeResponse = Readonly<{
  share_session_token: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerProfileImageUrl: string | null;
  resource_type: SharedResourceType;
  resource_id?: number;
}>;

export type ShareSessionResponse = Readonly<{
  share_session_id: string;
}>;

export type ShareSession = Readonly<{
  id: string;
  user_id: number;
  resource_type: SharedResourceType;
  resource_id?: number;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  last_accessed_at: string | null;
  access_count: number;
}>;

export type SharedSolutionSessionData = Readonly<{
  session_id: number | null;
  session_type: LubavSessionType;
  messages: SolutionSessionMessage[];
  main_response: MainResponse | null;
  collected_data: CollectedData;
  additional_resources: ResourcesContent;
  finished: boolean;
}>;

/**
 * API for shared session operations.
 * Session exchange doesn't require auth, but subsequent endpoints use Bearer token.
 */
export const sharedSessionApi = createApi({
  reducerPath: 'sharedSessionApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SharedSession'],
  endpoints: (build) => ({
    /**
     * Exchange a share_session_id for a JWT access token.
     * POST /shared/exchange-session
     * Body: { share_session_id: string }
     * Returns: { share_session_token: string }
     */
    exchangeSession: build.query<SessionExchangeResponse, { shareSessionId: string }>({
      query: ({ shareSessionId }) => ({
        url: 'shared/exchange-session',
        method: 'POST',
        body: { share_session_id: shareSessionId },
      }),
    }),
    /**
     * Create a new share session for a resource type.
     * POST /shared/:resourceType/create-session
     * Body: { resource_id?: number }
     * Returns: { share_session_id: string }
     */
    createShareSession: build.mutation<
      ShareSessionResponse,
      { resourceType: string; resourceId?: number }
    >({
      query: ({ resourceType, resourceId }) => ({
        url: `shared/${resourceType}/create-session`,
        method: 'POST',
        body: resourceId ? { resource_id: resourceId } : undefined,
      }),
      invalidatesTags: ['SharedSession'],
    }),
    /**
     * Revoke an existing share session for a resource type.
     * POST /shared/:resourceType/revoke-session
     * Body: { resource_id?: number }
     * Returns: { share_session_id: string }
     */
    revokeShareSession: build.mutation<
      ShareSessionResponse,
      { resourceType: string; resourceId?: number }
    >({
      query: ({ resourceType, resourceId }) => ({
        url: `shared/${resourceType}/revoke-session`,
        method: 'POST',
        body: resourceId ? { resource_id: resourceId } : undefined,
      }),
      invalidatesTags: ['SharedSession'],
    }),
    getMostRecentBlueprintData: build.query<
      MostRecentResponseSetAPIResponse,
      { accessToken: string }
    >({
      queryFn: async ({ accessToken }, _queryApi, _extraOptions) => {
        const customBaseQuery = baseQueryWithCustomToken(accessToken);

        return customBaseQuery(
          {
            url: 'shared/blueprint/career-user-response-set/most-recent',
            method: 'GET',
          },
          _queryApi,
          _extraOptions,
        ) as Promise<{ data: MostRecentResponseSetAPIResponse }>;
      },
    }),
    getMostRecentTrackAssessmentSummary: build.query<
      TrackAssessmentSummary,
      { accessToken: string }
    >({
      queryFn: async ({ accessToken }, _queryApi, _extraOptions) => {
        const customBaseQuery = baseQueryWithCustomToken(accessToken);

        return customBaseQuery(
          {
            url: 'shared/blueprint/track-assessment/summary',
            method: 'GET',
          },
          _queryApi,
          _extraOptions,
        ) as Promise<{ data: TrackAssessmentSummary }>;
      },
    }),
    /**
     * Get all active share sessions for a user by resource type.
     * GET /shared/:resourceType/sessions
     * Returns: ShareSession[]
     */
    getShareSessionsByResourceType: build.query<ShareSession[], { resourceType: string }>({
      query: ({ resourceType }) => `shared/${resourceType}/sessions`,
      providesTags: ['SharedSession'],
    }),
    /**
     * Get shared solution session data using the share token.
     * POST /api/solution-session/get-shared-session-data
     * Uses lubav-app endpoint with share token for auth.
     */
    getSharedSolutionSessionData: build.query<
      SharedSolutionSessionData,
      { accessToken: string; requestedSessionType?: string }
    >({
      queryFn: async ({ accessToken, requestedSessionType }, _queryApi, _extraOptions) => {
        const customBaseQuery = lubavAppBaseQueryWithCustomToken(accessToken);

        const result = await customBaseQuery(
          {
            url: '/api/solution-session/get-shared-session-data',
            method: 'POST',
            body: requestedSessionType ? { requested_session_type: requestedSessionType } : {},
          },
          _queryApi,
          _extraOptions,
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as SharedSolutionSessionData };
      },
    }),
    /**
     * Get shared office-hours session data using the share token.
     * POST /api/office-hours/get-shared-session-data
     */
    getSharedOfficeHoursSessionData: build.query<
      GetOfficeHoursSessionDataResponse,
      { accessToken: string }
    >({
      queryFn: async ({ accessToken }, _queryApi, _extraOptions) => {
        const customBaseQuery = lubavAppBaseQueryWithCustomToken(accessToken);

        const result = await customBaseQuery(
          {
            url: '/api/office-hours/get-shared-session-data',
            method: 'POST',
            body: {},
          },
          _queryApi,
          _extraOptions,
        );

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as GetOfficeHoursSessionDataResponse };
      },
    }),
  }),
});

export const {
  useExchangeSessionQuery,
  useCreateShareSessionMutation,
  useRevokeShareSessionMutation,
  useGetMostRecentBlueprintDataQuery,
  useGetMostRecentTrackAssessmentSummaryQuery,
  useGetShareSessionsByResourceTypeQuery,
  useGetSharedSolutionSessionDataQuery,
  useGetSharedOfficeHoursSessionDataQuery,
} = sharedSessionApi;
